import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Chip,
  FormControlLabel,
  Switch,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from "@mui/material";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import { DataGrid } from "@mui/x-data-grid";
import Card from "components/card";
import { getAllMaquilero } from "components-base/Maquileros/functions";
import { getAllSalidas } from "components-base/salidas/functions";
import {
  buildEntradaRecolectadaSummary,
  calculateSalidaCorteProgress,
  createEntradaCorte,
  filterEntradaCorteByFolio,
  hasRecolectadaDirectaEnTalla,
  getRecolectadaByTalla,
  getRecolectadaByTallaPrenda,
  getEntradaCorteByPedidoId,
  updateSalidaCortePercentage,
} from "./funciones";
import moment from "moment";
import "moment/locale/es";
moment.locale("es");

const getSalidaColumns = (onRegistroClick) => [
  {
    field: "acciones",
    headerName: "Acciones",
    width: 100,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <Tooltip title="Registro en el complejo" placement="top">
        <IconButton
          color="primary"
          onClick={() => onRegistroClick(params.row)}
        >
          <AppRegistrationIcon />
        </IconButton>
      </Tooltip>
    ),
  },
  {
    field: "folio",
    headerName: "Folio",
    width: 100,
  },
  {
    field: "pedido",
    headerName: "Código Pedido",
    width: 150,
    renderCell: (params) => params.row.pedido?.codigo || "—",
  },
  {
    field: "fechaSalida",
    headerName: "Fecha Salida",
    width: 150,
    renderCell: (params) =>
      params.row.fechaSalida
        ? moment(params.row.fechaSalida).format("DD/MM/YYYY")
        : "—",
  },
  {
    field: "fechaRetorno",
    headerName: "Fecha Retorno",
    width: 150,
    renderCell: (params) =>
      params.row.fechaRetorno
        ? moment(params.row.fechaRetorno).format("DD/MM/YYYY")
        : "—",
  },
  {
    field: "percentage",
    headerName: "% Completado",
    width: 120,
    renderCell: (params) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress
          variant="determinate"
          value={params.row.percentage || 0}
          size={30}
          color="success"
        />
        <Typography variant="caption">
          {`${Math.round(params.row.percentage || 0)}%`}
        </Typography>
      </Box>
    ),
  },
  {
    field: "chofer",
    headerName: "Operador",
    width: 200,
    renderCell: (params) =>
      params.row.chofer?.persona
        ? `${params.row.chofer.persona.nombre} ${params.row.chofer.persona.apellidoPaterno}`
        : "—",
  },
  {
    field: "completed",
    headerName: "Estado",
    width: 120,
    renderCell: (params) =>
      params.row.completed ? (
        <Chip label="Completada" color="success" size="small" />
      ) : (
        <Chip label="Pendiente" color="warning" size="small" />
      ),
  },
  {
    field: "details",
    headerName: "Producto / Talla / Cantidad",
    width: 350,
    renderCell: (params) => {
      const details = params.row.details?.data;
      if (!details || details.length === 0) return "—";
      return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {details.map((item, idx) => (
            <Typography variant="caption" key={idx}>
              {`${item.producto?.nombre || ""} / ${item.talla?.nombre || ""} / ${item.cantidadAsignada || 0}`}
            </Typography>
          ))}
        </Box>
      );
    },
  },
];

const EntradaMaquila = () => {
  const [maquileros, setMaquileros] = useState([]);
  const [selectedMaquilero, setSelectedMaquilero] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salidas, setSalidas] = useState([]);
  const [loadingSalidas, setLoadingSalidas] = useState(false);
  const [soloPendientes, setSoloPendientes] = useState(true);
  const [filtroFolio, setFiltroFolio] = useState("");
  const [filtroPedido, setFiltroPedido] = useState(null);
  const [openRegistro, setOpenRegistro] = useState(false);
  const [loadingRegistro, setLoadingRegistro] = useState(false);
  const [savingAjuste, setSavingAjuste] = useState(false);
  const [ordenSeleccionada, setOrdenSeleccionada] = useState(null);
  const [comentariosRegistro, setComentariosRegistro] = useState("");
  const [tallaAjusteSeleccionada, setTallaAjusteSeleccionada] = useState(null);
  const [prendaAjusteSeleccionada, setPrendaAjusteSeleccionada] = useState(null);
  const [tipoAjusteSeleccionado, setTipoAjusteSeleccionado] = useState({
    id: "sumar",
    nombre: "Sumar",
  });
  const [cantidadAjuste, setCantidadAjuste] = useState("");
  const [entradaRecolectadaResumen, setEntradaRecolectadaResumen] = useState({
    byTallaPrenda: {},
    byTalla: {},
    byPrenda: {},
    total: 0,
    rowsCount: 0,
  });

  const cargarResumenEntradaCorte = useCallback(async (orden) => {
    const pedidoId = orden?.pedido?.id;
    const folio = orden?.folio;

    if (!pedidoId) {
      const emptyResumen = {
        byTallaPrenda: {},
        byTalla: {},
        byPrenda: {},
        total: 0,
        rowsCount: 0,
      };
      setEntradaRecolectadaResumen(emptyResumen);
      return emptyResumen;
    }

    const entradaCorteData = await getEntradaCorteByPedidoId(pedidoId);

    const entradaCorteFiltrada = filterEntradaCorteByFolio(
      entradaCorteData,
      folio
    );

    const resumen = buildEntradaRecolectadaSummary(entradaCorteFiltrada);

    setEntradaRecolectadaResumen(resumen);
    return resumen;
  }, []);

  const handleRegistroClick = async (orden) => {
    setLoadingRegistro(true);
    setOpenRegistro(true);
    setOrdenSeleccionada(orden);

    try {
      console.log("[EntradaMaquila] Click en Registro en el complejo", {
        orden,
        salidaId: orden?.id,
        folio: orden?.folio,
        pedidoId: orden?.pedido?.id,
        maquileroId: orden?.maquilero?.id,
        choferId: orden?.chofer?.id,
      });

      await cargarResumenEntradaCorte(orden);

      setComentariosRegistro(orden.comments || "");
      setTallaAjusteSeleccionada(null);
      setPrendaAjusteSeleccionada(null);
      setTipoAjusteSeleccionado({ id: "sumar", nombre: "Sumar" });
      setCantidadAjuste("");
    } catch (error) {
      console.error("[EntradaMaquila] Error al preparar registro en el complejo:", error);
    } finally {
      setLoadingRegistro(false);
    }
  };

  const handleCloseRegistro = () => {
    setOpenRegistro(false);
    setLoadingRegistro(false);
    setOrdenSeleccionada(null);
    setComentariosRegistro("");
    setTallaAjusteSeleccionada(null);
    setPrendaAjusteSeleccionada(null);
    setTipoAjusteSeleccionado({ id: "sumar", nombre: "Sumar" });
    setCantidadAjuste("");
    setEntradaRecolectadaResumen({
      byTallaPrenda: {},
      byTalla: {},
      byPrenda: {},
      total: 0,
      rowsCount: 0,
    });
  };

  const handleGuardarAjuste = async () => {
    if (tipoAjusteSeleccionado?.id !== "sumar") {
      return;
    }

    const detalleBaseSeleccionado = (ordenSeleccionada?.details?.data || []).find(
      (item) => Number(item?.talla?.id) === Number(tallaAjusteSeleccionada?.id)
    );

    const prendaDetalleSeleccionada = (detalleBaseSeleccionado?.prendas || []).find(
      (item) => Number(item?.id) === Number(prendaAjusteSeleccionada?.id)
    );

    const detailsPayload = detalleBaseSeleccionado
      ? {
          talla: detalleBaseSeleccionado.talla || tallaAjusteSeleccionada || null,
          prendas: prendaDetalleSeleccionada ? [prendaDetalleSeleccionada] : [],
          cantidad: String(cantidadAjuste || 0),
          producto: detalleBaseSeleccionado.producto || null,
          cantidadAsignada: Number(detalleBaseSeleccionado.cantidadAsignada || 0),
        }
      : {};

    const driverId =
      ordenSeleccionada?.driver?.id ??
      ordenSeleccionada?.chofer?.id ??
      null;

    const payloadEntradaCorte = {
      id: 0,
      fechaEntrada: new Date().toISOString(),
      cantidad: Number(cantidadAjuste || 0),
      comments: "",
      folio: ordenSeleccionada?.folio ?? null,
      pedidos: {
        id: ordenSeleccionada?.pedido?.id ?? null,
      },
      driver: {
        id: driverId,
      },
      maquilero: {
        id: ordenSeleccionada?.maquilero?.id ?? null,
      },
      salida: {
        id: ordenSeleccionada?.id ?? null,
      },
      details: detailsPayload,
      prenda: prendaAjusteSeleccionada?.nombre || "",
      talla: tallaAjusteSeleccionada?.nombre || "",
    };

    try {
      setSavingAjuste(true);
      const response = await createEntradaCorte(payloadEntradaCorte);
      let ordenActualizada = ordenSeleccionada;

      if (selectedMaquilero?.id) {
        const salidasActualizadas = await fetchSalidasByMaquilero(selectedMaquilero.id);
        ordenActualizada =
          salidasActualizadas?.find(
            (item) => Number(item?.id) === Number(ordenSeleccionada?.id)
          ) || ordenSeleccionada;
        setOrdenSeleccionada(ordenActualizada);
      }

      const resumenActualizado = await cargarResumenEntradaCorte(ordenActualizada);

      const previewProgress = calculateSalidaCorteProgress(
        ordenActualizada,
        resumenActualizado
      );

      const updateResult = await updateSalidaCortePercentage(
        ordenActualizada,
        resumenActualizado
      );

      if (selectedMaquilero?.id) {
        const salidasRefrescadas = await fetchSalidasByMaquilero(selectedMaquilero.id);
        const ordenRefrescada =
          salidasRefrescadas?.find(
            (item) => Number(item?.id) === Number(ordenSeleccionada?.id)
          ) || ordenActualizada;
        setOrdenSeleccionada(ordenRefrescada);
      }

      setTallaAjusteSeleccionada(null);
      setPrendaAjusteSeleccionada(null);
      setTipoAjusteSeleccionado({ id: "sumar", nombre: "Sumar" });
      setCantidadAjuste("");
    } catch (error) {
      console.error("Error al guardar ajuste de entradaCorte:", error);
    } finally {
      setSavingAjuste(false);
    }
  };

  const salidaColumns = useMemo(
    () => getSalidaColumns(handleRegistroClick),
    []
  );

  useEffect(() => {
    const fetchMaquileros = async () => {
      try {
        const data = await getAllMaquilero();
        setMaquileros(data || []);
      } catch (error) {
        console.error("Error al obtener maquileros:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMaquileros();
  }, []);

  const fetchSalidasByMaquilero = useCallback(async (maquileroId) => {
    setLoadingSalidas(true);
    try {
      const allSalidas = await getAllSalidas();
      const filtered = (allSalidas || []).filter(
        (salida) => Number(salida.maquilero?.id) === Number(maquileroId)
      );
      setSalidas(filtered);
      return filtered;
    } catch (error) {
      console.error("Error al obtener salidas:", error);
      setSalidas([]);
      return [];
    } finally {
      setLoadingSalidas(false);
    }
  }, []);

  const handleSelectMaquilero = (e, value) => {
    setSelectedMaquilero(value);
    setFiltroFolio("");
    setFiltroPedido(null);
    if (value) {
      fetchSalidasByMaquilero(value.id);
    } else {
      setSalidas([]);
    }
  };

  const pedidosDisponibles = useMemo(() => {
    const map = new Map();
    salidas.forEach((s) => {
      if (s.pedido?.id && !map.has(s.pedido.id)) {
        map.set(s.pedido.id, s.pedido);
      }
    });
    return Array.from(map.values());
  }, [salidas]);

  const salidasFiltradas = useMemo(() => {
    let result = salidas;
    if (soloPendientes) {
      result = result.filter((s) => !s.completed);
    }
    if (filtroFolio.trim()) {
      result = result.filter((s) =>
        String(s.folio || "").toLowerCase().includes(filtroFolio.trim().toLowerCase())
      );
    }
    if (filtroPedido) {
      result = result.filter(
        (s) => Number(s.pedido?.id) === Number(filtroPedido.id)
      );
    }
    return result;
  }, [salidas, soloPendientes, filtroFolio, filtroPedido]);

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Card extra={"w-full p-6"}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Entrada de Maquila
        </Typography>

        <Box sx={{ maxWidth: 500 }}>
          <Autocomplete
            id="maquilero"
            name="maquilero"
            options={maquileros}
            loading={loading}
            value={selectedMaquilero}
            isOptionEqualToValue={(option, value) =>
              Number(option.id) === Number(value.id)
            }
            getOptionLabel={(option) =>
              `${option.persona?.nombre || ""} ${option.persona?.apellidoPaterno || ""} ${option.persona?.apellidoMaterno || ""}`.trim()
            }
            onChange={handleSelectMaquilero}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar Maquilero"
                placeholder="Escribe el nombre del maquilero..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            fullWidth
            noOptionsText="No se encontraron maquileros"
            loadingText="Cargando maquileros..."
          />
        </Box>

        {selectedMaquilero && (
          <Paper elevation={2} sx={{ mt: 3, p: 3, maxWidth: 500 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              Maquilero seleccionado
            </Typography>
            <Typography>
              <strong>Nombre:</strong>{" "}
              {`${selectedMaquilero.persona?.nombre || ""} ${selectedMaquilero.persona?.apellidoPaterno || ""} ${selectedMaquilero.persona?.apellidoMaterno || ""}`}
            </Typography>
            {selectedMaquilero.codigo && (
              <Typography>
                <strong>Código:</strong> {selectedMaquilero.codigo}
              </Typography>
            )}
            {selectedMaquilero.persona?.direccion && (
              <Typography>
                <strong>Dirección:</strong>{" "}
                {selectedMaquilero.persona.direccion}
              </Typography>
            )}
            {selectedMaquilero.persona?.rfc && (
              <Typography>
                <strong>RFC:</strong> {selectedMaquilero.persona.rfc}
              </Typography>
            )}
            {selectedMaquilero.capacidad != null && (
              <Typography>
                <strong>Capacidad:</strong> {selectedMaquilero.capacidad}
              </Typography>
            )}
          </Paper>
        )}
      </Card>

      {selectedMaquilero && (
        <Card extra={"w-full p-6 mt-4"}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Órdenes de Salida asignadas
          </Typography>

          <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={soloPendientes}
                    onChange={(e) => setSoloPendientes(e.target.checked)}
                    color="warning"
                  />
                }
                label="Solo pendientes"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Buscar por folio"
                placeholder="Ej. 001"
                size="small"
                fullWidth
                value={filtroFolio}
                onChange={(e) => setFiltroFolio(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Autocomplete
                size="small"
                options={pedidosDisponibles}
                value={filtroPedido}
                getOptionLabel={(option) => option.codigo || ""}
                isOptionEqualToValue={(option, value) =>
                  Number(option.id) === Number(value.id)
                }
                onChange={(e, value) => setFiltroPedido(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Filtrar por pedido" placeholder="Selecciona un pedido..." />
                )}
                noOptionsText="Sin pedidos"
                fullWidth
              />
            </Grid>
          </Grid>

          {loadingSalidas ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 4 }}>
              <CircularProgress size={24} />
              <Typography>Cargando órdenes de salida...</Typography>
            </Box>
          ) : salidasFiltradas.length > 0 || salidas.length > 0 ? (
            <Box sx={{ height: 500, width: "100%" }}>
              <DataGrid
                rows={salidasFiltradas}
                columns={salidaColumns}
                initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                pageSize={10}
                rowsPerPageOptions={[5, 10, 15, 20]}
                disableSelectionOnClick
                rowHeight={60}
                className="dark:text-white"
              />
            </Box>
          ) : (
            <Typography color="text.secondary" sx={{ py: 4 }}>
              {salidas.length === 0
                ? "No se encontraron órdenes de salida para este maquilero."
                : "No se encontraron órdenes con los filtros aplicados."}
            </Typography>
          )}
        </Card>
      )}

      <Dialog
        open={openRegistro}
        onClose={handleCloseRegistro}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>Registro en el complejo</DialogTitle>
        <DialogContent dividers>
          {loadingRegistro ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 3 }}>
              <CircularProgress size={24} />
              <Typography>Cargando información del registro...</Typography>
            </Box>
          ) : ordenSeleccionada && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Info general */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Folio:</strong> {ordenSeleccionada.folio}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Código Pedido:</strong>{" "}
                    {ordenSeleccionada.pedido?.codigo || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Fecha Salida:</strong>{" "}
                    {ordenSeleccionada.fechaSalida
                      ? moment(ordenSeleccionada.fechaSalida).format("DD/MM/YYYY")
                      : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography>
                    <strong>Fecha Retorno:</strong>{" "}
                    {ordenSeleccionada.fechaRetorno
                      ? moment(ordenSeleccionada.fechaRetorno).format("DD/MM/YYYY")
                      : "—"}
                  </Typography>
                </Grid>
              </Grid>

              <Divider />

              {/* Responsables */}
              <Typography variant="subtitle1" fontWeight="bold">
                Responsables
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Chofer / Operador
                    </Typography>
                    <Typography>
                      {ordenSeleccionada.chofer?.persona
                        ? `${ordenSeleccionada.chofer.persona.nombre} ${ordenSeleccionada.chofer.persona.apellidoPaterno} ${ordenSeleccionada.chofer.persona.apellidoMaterno || ""}`.trim()
                        : "—"}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Supervisor
                    </Typography>
                    <Typography>
                      {ordenSeleccionada.supervisor?.persona
                        ? `${ordenSeleccionada.supervisor.persona.nombre} ${ordenSeleccionada.supervisor.persona.apellidoPaterno} ${ordenSeleccionada.supervisor.persona.apellidoMaterno || ""}`.trim()
                        : "Sin supervisor asignado"}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Divider />

              {/* Tabla de prendas por talla — columnas dinámicas por prenda */}
              <Typography variant="subtitle1" fontWeight="bold">
                Detalle de prendas por talla
              </Typography>
              {ordenSeleccionada.details?.data?.length > 0 ? (() => {
                const prendas = ordenSeleccionada.details?.prendas || [];
                const data = ordenSeleccionada.details.data;
                const tiposAjuste = [
                  { id: "sumar", nombre: "Sumar" },
                  { id: "restar", nombre: "Restar" },
                ];
                const tallasDisponibles = data
                  .map((item) => item.talla)
                  .filter(
                    (talla, index, arr) =>
                      arr.findIndex((item) => Number(item?.id) === Number(talla?.id)) === index
                  );

                const getPrendaAsignada = (item, prendaId) => {
                  const prendaItem = (item.prendas || []).find(
                    (pr) => Number(pr.id) === Number(prendaId)
                  );
                  return Number(prendaItem?.cantidadAsignada || 0);
                };

                const getPrendaRecolectadaBase = (item, prendaId) => {
                  const recolectadaDirecta = getRecolectadaByTallaPrenda(
                    entradaRecolectadaResumen,
                    item?.talla,
                    (item.prendas || []).find((pr) => Number(pr.id) === Number(prendaId)) || {
                      id: prendaId,
                    }
                  );

                  if (recolectadaDirecta != null) {
                    return Number(recolectadaDirecta);
                  }

                  if (hasRecolectadaDirectaEnTalla(entradaRecolectadaResumen, item?.talla)) {
                    return 0;
                  }

                  const recolectadaPorTalla = getRecolectadaByTalla(
                    entradaRecolectadaResumen,
                    item?.talla
                  );
                  const asignadaPrenda = Number(
                    (item.prendas || []).find((pr) => Number(pr.id) === Number(prendaId))
                      ?.cantidadAsignada || 0
                  );
                  const asignadas = Number(item.cantidadAsignada || 0);
                  const recolectadas = Number(item.cantidadRecolectada || 0);
                  const prendaItem = (item.prendas || []).find(
                    (pr) => Number(pr.id) === Number(prendaId)
                  );

                  if (recolectadaPorTalla != null) {
                    if (!asignadaPrenda || !asignadas) return 0;
                    return Number(((asignadaPrenda / asignadas) * Number(recolectadaPorTalla)).toFixed(2));
                  }

                  if (prendaItem?.cantidadRecolectada != null) {
                    return Number(prendaItem.cantidadRecolectada || 0);
                  }

                  const asignadaPrendaFallback = Number(prendaItem?.cantidadAsignada || 0);
                  if (!asignadaPrendaFallback || !asignadas) return 0;

                  const proporcional = (asignadaPrendaFallback / asignadas) * recolectadas;
                  return Number(proporcional.toFixed(2));
                };

                const getPrendaRecolectada = (item, prendaId, rowIdx) => {
                  const base = getPrendaRecolectadaBase(item, prendaId);
                  return Number(base.toFixed(2));
                };

                const getColorPrenda = (index) => {
                  const colors = [
                    {
                      header: "rgba(25, 118, 210, 0.16)",
                      cell: "rgba(25, 118, 210, 0.06)",
                      total: "rgba(25, 118, 210, 0.12)",
                    },
                    {
                      header: "rgba(46, 125, 50, 0.16)",
                      cell: "rgba(46, 125, 50, 0.06)",
                      total: "rgba(46, 125, 50, 0.12)",
                    },
                  ];
                  return colors[index % colors.length];
                };

                const detalleSeleccionadoParaAjuste = data.find(
                  (item) => Number(item?.talla?.id) === Number(tallaAjusteSeleccionada?.id)
                );
                const cantidadAsignadaAjuste = detalleSeleccionadoParaAjuste
                  ? getPrendaAsignada(
                      detalleSeleccionadoParaAjuste,
                      prendaAjusteSeleccionada?.id
                    )
                  : 0;
                const cantidadRecolectadaAjuste = detalleSeleccionadoParaAjuste
                  ? getPrendaRecolectada(
                      detalleSeleccionadoParaAjuste,
                      prendaAjusteSeleccionada?.id,
                      data.findIndex(
                        (item) =>
                          Number(item?.talla?.id) ===
                          Number(tallaAjusteSeleccionada?.id)
                      )
                    )
                  : 0;
                const maxCantidadAjuste = Math.max(
                  0,
                  Number(
                    (cantidadAsignadaAjuste - cantidadRecolectadaAjuste).toFixed(2)
                  )
                );

                return (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "action.hover" }}>
                            <TableCell align="center"><strong>Talla</strong></TableCell>
                            {prendas.map((p, i) => (
                              <React.Fragment key={i}>
                                <TableCell align="center" sx={{ backgroundColor: getColorPrenda(i).header }}>
                                  <strong>{p.nombre}</strong>
                                  <br />
                                  <strong>Asignadas</strong>
                                </TableCell>
                                <TableCell align="center" sx={{ backgroundColor: getColorPrenda(i).header }}>
                                  <strong>{p.nombre}</strong>
                                  <br />
                                  <strong>Recolectadas</strong>
                                </TableCell>
                                <TableCell align="center" sx={{ backgroundColor: getColorPrenda(i).header }}>
                                  <strong>{p.nombre}</strong>
                                  <br />
                                  <strong>Faltantes</strong>
                                </TableCell>
                              </React.Fragment>
                            ))}
                            <TableCell align="center"><strong>Total Asignadas</strong></TableCell>
                            <TableCell align="center"><strong>Total Recolectadas</strong></TableCell>
                            <TableCell align="center"><strong>Total Faltantes</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {data.map((item, idx) => {
                            const asignadas = item.cantidadAsignada || 0;
                            const totalRecolectadasPrendaRow = prendas.reduce(
                              (sum, p) => sum + getPrendaRecolectada(item, p.id, idx),
                              0
                            );
                            const faltantes = asignadas - totalRecolectadasPrendaRow;

                            return (
                              <TableRow key={idx}>
                                <TableCell align="center">{item.talla?.nombre || "—"}</TableCell>
                                {prendas.map((p, i) => {
                                  const asignadaPrenda = getPrendaAsignada(item, p.id);
                                  const recolectadaPrenda = getPrendaRecolectada(item, p.id, idx);
                                  const prendaColor = getColorPrenda(i);
                                  return (
                                    <React.Fragment key={i}>
                                      <TableCell align="center" sx={{ backgroundColor: prendaColor.cell }}>
                                        {asignadaPrenda}
                                      </TableCell>
                                      <TableCell align="center" sx={{ minWidth: 120, backgroundColor: prendaColor.cell }}>
                                        {recolectadaPrenda}
                                      </TableCell>
                                      <TableCell align="center" sx={{ backgroundColor: prendaColor.cell }}>
                                        <Chip
                                          label={Number((asignadaPrenda - recolectadaPrenda).toFixed(2))}
                                          size="small"
                                          sx={{
                                            backgroundColor: prendaColor.total,
                                            color: "text.primary",
                                            fontWeight: 600,
                                          }}
                                        />
                                      </TableCell>
                                    </React.Fragment>
                                  );
                                })}
                                <TableCell align="center">{asignadas}</TableCell>
                                <TableCell align="center">
                                  {Number(totalRecolectadasPrendaRow.toFixed(2))}
                                </TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={Number(faltantes.toFixed(2))}
                                    size="small"
                                    color={faltantes > 0 ? "error" : "success"}
                                    variant="outlined"
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow sx={{ backgroundColor: "action.hover" }}>
                            <TableCell align="center"><strong>TOTAL</strong></TableCell>
                            {prendas.map((p, i) => {
                              const totalPrenda = data.reduce((sum, item) => {
                                return sum + getPrendaAsignada(item, p.id);
                              }, 0);
                              const totalRecolectadaPrenda = data.reduce(
                                (sum, item, rowIdx) =>
                                  sum + getPrendaRecolectada(item, p.id, rowIdx),
                                0
                              );
                              return (
                                <React.Fragment key={i}>
                                  <TableCell align="center" sx={{ backgroundColor: getColorPrenda(i).total }}>
                                    <strong>{totalPrenda}</strong>
                                  </TableCell>
                                  <TableCell align="center" sx={{ backgroundColor: getColorPrenda(i).total }}>
                                    <strong>{Number(totalRecolectadaPrenda.toFixed(2))}</strong>
                                  </TableCell>
                                  <TableCell align="center" sx={{ backgroundColor: getColorPrenda(i).total }}>
                                    <Chip
                                      label={Number((totalPrenda - totalRecolectadaPrenda).toFixed(2))}
                                      size="small"
                                      sx={{
                                        backgroundColor: getColorPrenda(i).header,
                                        color: "text.primary",
                                        fontWeight: 600,
                                      }}
                                    />
                                  </TableCell>
                                </React.Fragment>
                              );
                            })}
                            {(() => {
                              const totalAsignadas = data.reduce(
                                (sum, item) => sum + Number(item.cantidadAsignada || 0),
                                0
                              );
                              const totalRecolectadas = data.reduce(
                                (sum, item, rowIdx) =>
                                  sum +
                                  prendas.reduce(
                                    (sub, p) => sub + getPrendaRecolectada(item, p.id, rowIdx),
                                    0
                                  ),
                                0
                              );
                              const totalFaltantes = totalAsignadas - totalRecolectadas;
                              return (
                                <>
                                  <TableCell
                                    align="center"
                                    sx={{ backgroundColor: "rgba(255, 167, 38, 0.16)" }}
                                  >
                                    <strong>{totalAsignadas}</strong>
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{ backgroundColor: "rgba(255, 167, 38, 0.16)" }}
                                  >
                                    <strong>
                                      <Chip
                                        label={Number(totalRecolectadas.toFixed(2))}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                      />
                                    </strong>
                                  </TableCell>
                                  <TableCell
                                    align="center"
                                    sx={{ backgroundColor: "rgba(255, 167, 38, 0.16)" }}
                                  >
                                    <strong>
                                      <Chip
                                        label={Number(totalFaltantes.toFixed(2))}
                                        size="small"
                                        color={totalFaltantes > 0 ? "error" : "success"}
                                        variant="outlined"
                                      />
                                    </strong>
                                  </TableCell>
                                </>
                              );
                            })()}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                        Ajuste de prendas recolectadas
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}>
                          <Autocomplete
                            options={tallasDisponibles}
                            value={tallaAjusteSeleccionada}
                            getOptionLabel={(option) => option?.nombre || ""}
                            isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                            onChange={(e, value) => setTallaAjusteSeleccionada(value)}
                            renderInput={(params) => (
                              <TextField {...params} label="Talla" placeholder="Selecciona una talla" />
                            )}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Autocomplete
                            options={prendas}
                            value={prendaAjusteSeleccionada}
                            getOptionLabel={(option) => option?.nombre || ""}
                            isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                            onChange={(e, value) => setPrendaAjusteSeleccionada(value)}
                            renderInput={(params) => (
                              <TextField {...params} label="Prenda" placeholder="Selecciona una prenda" />
                            )}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Autocomplete
                            options={tiposAjuste}
                            value={tipoAjusteSeleccionado}
                            getOptionLabel={(option) => option?.nombre || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            onChange={(e, value) => setTipoAjusteSeleccionado(value || tiposAjuste[0])}
                            renderInput={(params) => (
                              <TextField {...params} label="Acción" placeholder="Selecciona una acción" />
                            )}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <TextField
                            type="number"
                            label="Cantidad"
                            sx={{ mt: 3 }}
                            value={cantidadAjuste}
                            onChange={(e) => {
                              const rawValue = Number(e.target.value || 0);
                              if (Number.isNaN(rawValue)) {
                                setCantidadAjuste("");
                                return;
                              }

                              const limitedValue = Math.min(
                                Math.max(rawValue, 0),
                                maxCantidadAjuste
                              );
                              setCantidadAjuste(String(limitedValue));
                            }}
                            inputProps={{ min: 0, max: maxCantidadAjuste, step: 1 }}
                            helperText={
                              tallaAjusteSeleccionada && prendaAjusteSeleccionada
                                ? `Maximo permitido: ${maxCantidadAjuste}`
                                : "Selecciona talla y prenda"
                            }
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={handleGuardarAjuste}
                            disabled={
                              savingAjuste ||
                              !tallaAjusteSeleccionada ||
                              !prendaAjusteSeleccionada ||
                              !tipoAjusteSeleccionado ||
                              !Number(cantidadAjuste)
                            }
                          >
                            {savingAjuste ? "Guardando..." : "Guardar ajuste"}
                          </Button>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Box>
                );
              })() : (
                <Typography color="text.secondary">
                  Sin detalle de prendas.
                </Typography>
              )}

              <Divider />

              {/* Comentarios */}
              <Typography variant="subtitle1" fontWeight="bold">
                Comentarios
              </Typography>
              <TextField
                multiline
                rows={3}
                fullWidth
                placeholder="Escribe un comentario sobre esta entrada..."
                value={comentariosRegistro}
                onChange={(e) => setComentariosRegistro(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRegistro}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EntradaMaquila;